import api from '../API';

/** Default chunk size for Girder uploads (32 MB), under nginx 100 MB limit. */
const DEFAULT_CHUNK_SIZE = 32 * 1024 * 1024;

/**
 * Upload file in chunks per Girder convention. Returns the final file/item info from the last response.
 * @param {File} file
 * @param {string} uploadId - from POST /file response
 * @param {object} upload - full upload document from POST /file (may have chunkSize / minimumChunkSize)
 * @param {function} onProgress - optional ({ status, progress }) for progress updates
 * @returns {Promise<object>} - final response (file document when complete); has _id or itemId for the item
 */
export async function uploadFileInChunks(file, uploadId, upload, onProgress = () => {}) {
    const chunkSize = upload.chunkSize || upload.minimumChunkSize || DEFAULT_CHUNK_SIZE;
    const headers = {};
    if (typeof localStorage !== 'undefined' && localStorage.girderToken) {
        headers['Girder-Token'] = localStorage.girderToken;
    }
    let start = 0;
    const totalSize = file.size;
    let lastResult = null;

    while (start < totalSize) {
        const end = Math.min(start + chunkSize, totalSize);
        const chunk = file.slice(start, end);
        const chunkParams = { uploadId, offset: String(start) };
        const chunkUrl = api.url('/file/chunk', { query: chunkParams });
        const chunkResponse = await fetch(chunkUrl, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/octet-stream' },
            body: chunk
        });
        if (!chunkResponse.ok) {
            throw new Error(`Failed to upload chunk: ${chunkResponse.statusText}`);
        }
        const result = await chunkResponse.json();
        lastResult = result;
        start = end;
        if (typeof onProgress === 'function') {
            onProgress({ status: 'uploading', progress: totalSize > 0 ? Math.round((end / totalSize) * 100) : 100 });
        }
        // Girder returns file document when upload is finalized (last chunk), otherwise upload document with received
        const isUploadDoc = result.received != null && result.size != null && result.received < result.size;
        if (!isUploadDoc) {
            break;
        }
    }
    return lastResult;
}
